import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import {
  Badge,
  Breadcrumb,
  Button,
  Label,
  Modal,
  Select,
  Spinner,
  TextInput,
} from "flowbite-react";
import { HiHome, HiFilter, HiChevronDown, HiChevronUp } from "react-icons/hi";
import { Fragment, createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingPage from "@/components/LoadingPage";
import TablePagination from "@/components/Pagination";
import SuperAdminCompanyBar from "@/components/SuperAdminCompanyBar";
import SelectCompanyPrompt from "@/components/SelectCompanyPrompt";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isSuperAdminUser } from "@/constants/agent";
import {
  canFetchRecord,
  useGetAuditLogFiltersQuery,
  useGetAuditLogsQuery,
  useGetAuditRecordQuery,
  type AuditLogEntry,
} from "@/api/auditLogApi/auditLog";

const ACTION_COLORS: Record<string, string> = {
  create: "success",
  update: "info",
  delete: "warning",
  hard_delete: "failure",
  restore: "purple",
  assign: "indigo",
  unassign: "gray",
  connect: "success",
  disconnect: "warning",
  rotate_secret: "pink",
  login: "dark",
  logout: "dark",
};

/**
 * Flatten one level of nested meta so each leaf gets its own chip.
 *
 * Meta often carries the request's route params, and the ids in there are
 * sometimes the only record of what a change actually touched — a KB
 * unassign, for instance, names the issue as its entity while the solution it
 * removed lives in `params`. Rendering that object through formatValue turned
 * it into a JSON blob nobody reads.
 */
const flattenMeta = (meta: Record<string, unknown>): Array<[string, unknown]> =>
  Object.entries(meta).flatMap(([key, value]) =>
    value !== null && typeof value === "object" && !Array.isArray(value)
      ? Object.entries(value as Record<string, unknown>).map(
          ([inner, innerValue]) => [`${key}.${inner}`, innerValue] as [string, unknown]
        )
      : [[key, value] as [string, unknown]]
  );

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/** Types that have their own detail page in the panel. */
const entityLink = (entityType: string, id: string | number | null | undefined): string | null => {
  if (id === null || id === undefined || id === "") return null;
  switch (entityType) {
    case "agent":
      return `/agents/${id}`;
    case "group":
      return `/groups/${id}`;
    case "client_group":
      return `/client-groups/${id}`;
    case "subcategory":
      return `/subcategories/${id}`;
    case "ticket":
      return `/tickets/${id}`;
    case "shift_hours":
      return `/work-shifts/${id}`;
    default:
      return null;
  }
};

/** Which entity a foreign-key field points at, so its value can be a link too. */
const FIELD_ENTITY: Record<string, string> = {
  agent_id: "agent",
  agent_ids: "agent",
  actor_agent_id: "agent",
  created_by_agent_id: "agent",
  last_assigned_agent_id: "agent",
  assigned_agent_id: "agent",
  group_id: "group",
  group_ids: "group",
  client_group_id: "client_group",
  subcategory_id: "subcategory",
  last_subcategory_id: "subcategory",
  ticket_id: "ticket",
  shift_id: "shift_hours",
  client_id: "client",
  client_ids: "client",
  category_id: "category",
  last_category_id: "category",
  update_reason_id: "update_reason",
};

type OpenRecord = (entityType: string, id: string | number) => void;
const RecordOpenContext = createContext<OpenRecord>(() => {});

/** Clickable id: opens the record in a dialog, without leaving the log. */
const IdButton = ({
  entityType,
  id,
  children,
}: {
  entityType: string;
  id: string | number;
  children: React.ReactNode;
}) => {
  const open = useContext(RecordOpenContext);
  if (!canFetchRecord(entityType)) return <>{children}</>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        open(entityType, id);
      }}
      className="text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-400"
    >
      {children}
    </button>
  );
};

const HIDDEN_RECORD_KEYS = new Set([
  "agent_password",
  "client_password",
  "password",
  "tenant_secret",
  "api_key",
  "secret",
]);

/** Fetches one record and shows its fields; the user never leaves the log. */
const RecordDialog = ({
  target,
  onClose,
}: {
  target: { entityType: string; id: string | number } | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["auditLog", "common"]);
  const { data, isFetching, isError } = useGetAuditRecordQuery(
    target ? { entity_type: target.entityType, id: target.id } : { entity_type: "", id: "" },
    { skip: !target }
  );
  if (!target) return null;

  const typeLabel = t(`auditLog:entities.${target.entityType}` as any, {
    defaultValue: target.entityType,
  });
  const pageLink = entityLink(target.entityType, target.id);
  const rows = data
    ? Object.entries(data).filter(([k]) => !HIDDEN_RECORD_KEYS.has(k))
    : [];

  const renderField = (v: unknown): string => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "✓" : "✗";
    if (Array.isArray(v)) {
      return v
        .map((x) =>
          x && typeof x === "object"
            ? ((x as any).group_name ?? (x as any).name ?? (x as any).role ?? JSON.stringify(x))
            : String(x)
        )
        .join(", ");
    }
    if (typeof v === "object") {
      const o = v as any;
      return o.company_name ?? o.group_name ?? o.name ?? o.display_name ?? JSON.stringify(o);
    }
    return String(v);
  };

  return (
    <Modal show onClose={onClose} size="2xl">
      <Modal.Header>
        {typeLabel} #{String(target.id)}
      </Modal.Header>
      <Modal.Body>
        {isFetching ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : isError || !data ? (
          <p className="text-sm text-gray-500">{t("auditLog:recordNotFound")}</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-left text-sm">
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(([k, v]) => (
                  <tr key={k}>
                    <td className="w-1/3 py-1.5 pr-4 align-top font-mono text-xs text-gray-500 dark:text-gray-400">
                      {k}
                    </td>
                    <td className="break-words py-1.5 text-gray-800 dark:text-gray-200">
                      {renderField(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-end">
        {pageLink && (
          <Button as={Link} to={pageLink} color="primary" onClick={onClose}>
            {t("auditLog:openPage")}
          </Button>
        )}
        <Button color="light" onClick={onClose}>
          {t("common:close", { defaultValue: "Close" })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Renders a change value; id fields become links to the record they point at. */
const ValueCell = ({ field, value }: { field: string; value: unknown }) => {
  const entity = FIELD_ENTITY[field];
  if (entity && Array.isArray(value)) {
    return (
      <span className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <IdButton key={i} entityType={entity} id={v as any}>
            #{String(v)}
          </IdButton>
        ))}
      </span>
    );
  }
  if (entity && (typeof value === "number" || typeof value === "string")) {
    return <IdButton entityType={entity} id={value}>#{String(value)}</IdButton>;
  }
  return <>{formatValue(value)}</>;
};

/**
 * The row a link change actually touched.
 *
 * An assign/unassign names the parent as its entity — removing solution 1261
 * from issue 1270 is logged against 1270 — so without this the reader sees the
 * parent's id where they expect the thing that changed. Newer rows carry
 * `meta.target_id`; older ones only have the route params, which is where the
 * id was hiding all along.
 */
const linkTarget = (
  changes: any
): { resource: string | null; id: string | null } | null => {
  const meta = changes?.meta;
  if (!meta) return null;

  const id =
    meta.target_id ??
    Object.entries(meta.params ?? {}).find(
      ([k]) => /^(solution|child|target).*id$/i.test(k) || /_id$/i.test(k)
    )?.[1];

  if (id === undefined || id === null || id === "") return null;
  return { resource: meta.target_resource ?? null, id: String(id) };
};

/** "field: before → after" rows for an update, plain key/value for the rest. */
const ChangeDetails = ({ entry }: { entry: AuditLogEntry }) => {
  const { t } = useTranslation(["auditLog"]);
  const [raw, setRaw] = useState(false);
  const changes = entry.changes;
  if (!changes) {
    return (
      <span className="text-sm text-gray-400">{t("auditLog:noDetails")}</span>
    );
  }

  const before = changes.before ?? {};
  const after = changes.after ?? {};
  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)])
  );

  const isLinkChange = entry.action === "assign" || entry.action === "unassign";
  const target = isLinkChange ? linkTarget(changes) : null;

  return (
    <div className="flex flex-col gap-2 text-sm">
      {/* Lead with the affected row. Older entries also carry the parent's
          whole snapshot under `before`, which reads as a previous value and is
          exactly what makes an unassign look like it happened to the parent —
          so that column is left out for these actions. */}
      {target && (
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase text-gray-500">
            {t("auditLog:affectedRecord")}
          </span>
          <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
            {target.resource ? `${target.resource} ` : ""}#{target.id}
          </span>
        </div>
      )}
      {!raw && keys.length > 0 && (
        <table className="w-full text-left">
          <thead className="text-xs uppercase text-gray-500">
            <tr>
              <th className="py-1 pr-4">{t("auditLog:field")}</th>
              {entry.action === "update" && !isLinkChange && (
                <th className="py-1 pr-4">{t("auditLog:before")}</th>
              )}
              <th className="py-1 pr-4">
                {entry.action === "update"
                  ? t("auditLog:after")
                  : t("auditLog:value")}
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-1 pr-4 font-mono text-xs text-gray-600 dark:text-gray-300">
                  {k}
                </td>
                {/* Clipping to one line here defeats the point of the log:
                    this is where someone checks what a change actually
                    replaced, and a truncated old value can't be checked. */}
                {entry.action === "update" && !isLinkChange && (
                  <td className="max-w-md break-words py-1 pr-4 align-top text-red-600 dark:text-red-400">
                    <ValueCell field={k} value={before[k]} />
                  </td>
                )}
                <td className="max-w-md break-words py-1 pr-4 align-top text-green-700 dark:text-green-400">
                  <ValueCell
                    field={k}
                    value={
                      entry.action === "update" || after[k] !== undefined
                        ? after[k]
                        : before[k]
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!raw && changes.meta && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span className="font-semibold">{t("auditLog:meta")}:</span>
          {flattenMeta(changes.meta as Record<string, unknown>).map(([k, v]) => (
            <span key={k}>
              <span className="font-mono">{k}</span>={" "}
              <ValueCell field={k} value={v} />
            </span>
          ))}
        </div>
      )}
      {raw && (
        <pre className="max-h-80 overflow-auto rounded bg-gray-100 p-2 text-xs dark:bg-gray-900">
          {JSON.stringify(changes, null, 2)}
        </pre>
      )}
      <button
        type="button"
        className="self-start text-xs text-blue-600 hover:underline dark:text-blue-400"
        onClick={() => setRaw(!raw)}
      >
        {raw ? t("auditLog:showTable") : t("auditLog:showRaw")}
      </button>
      {(entry.ip || entry.user_agent) && (
        <div className="text-xs text-gray-400">
          {entry.ip && <span>IP: {entry.ip}</span>}
          {entry.user_agent && (
            <span className="ml-3 truncate">UA: {entry.user_agent}</span>
          )}
        </div>
      )}
    </div>
  );
};

const AuditLogPage = () => {
  const { t, i18n } = useTranslation(["auditLog", "common"]);
  const user = useCurrentUser();
  const isSuperAdmin = isSuperAdminUser(user);
  const { selectedCompany } = useSelectedCompany();
  const needsCompany = isSuperAdmin && !selectedCompany;

  const [page, setPage] = useState(1);
  const pageSize = 30;
  const [showFilters, setShowFilters] = useState(false);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [recordTarget, setRecordTarget] = useState<{ entityType: string; id: string | number } | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [entityType, action, debouncedSearch, from, to, selectedCompany]);

  const query = useMemo(
    () => ({
      page,
      pageSize,
      entity_type: entityType || undefined,
      action: action || undefined,
      search: debouncedSearch || undefined,
      from: from || undefined,
      to: to || undefined,
      ...(isSuperAdmin && selectedCompany
        ? { company_id: Number(selectedCompany.value) }
        : {}),
    }),
    [page, entityType, action, debouncedSearch, from, to, isSuperAdmin, selectedCompany]
  );

  const { data, isLoading, isFetching } = useGetAuditLogsQuery(query, {
    skip: needsCompany,
    refetchOnMountOrArgChange: true,
  });
  const { data: filters } = useGetAuditLogFiltersQuery(undefined, {
    skip: needsCompany,
  });

  const clearFilters = () => {
    setEntityType("");
    setAction("");
    setSearch("");
    setFrom("");
    setTo("");
  };

  const toggleFilters = () => {
    if (showFilters) clearFilters();
    setShowFilters(!showFilters);
  };

  const labelFor = (ns: "entities" | "actions", key: string) =>
    t(`auditLog:${ns}.${key}` as any, { defaultValue: key });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language === "tr" ? "tr-TR" : "en-GB");

  return (
    <NavbarSidebarLayout>
      <RecordOpenContext.Provider value={(entityType, id) => setRecordTarget({ entityType, id })}>
      <RecordDialog target={recordTarget} onClose={() => setRecordTarget(null)} />
      <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="col-span-full">
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item href="/">
              <div className="flex items-center gap-x-3">
                <HiHome className="text-xl" />
                <span className="dark:text-white">{t("common:home")}</span>
              </div>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{t("auditLog:title")}</Breadcrumb.Item>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                {t("auditLog:title")}
              </h1>
              <p className="text-gray-500">{t("auditLog:subtitle")}</p>
            </div>
          </div>

          {isSuperAdmin && <SuperAdminCompanyBar />}

          <div className="mt-6 flex justify-start">
            <Button color="light" onClick={toggleFilters}>
              <HiFilter className="mr-2 h-5 w-5" />
              {showFilters ? t("common:hideFilters") : t("common:showFilters")}
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <Label htmlFor="audit-search" value={t("auditLog:search")} />
                <TextInput
                  id="audit-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("auditLog:searchPlaceholder")}
                />
              </div>
              <div>
                <Label htmlFor="audit-entity" value={t("auditLog:entityType")} />
                <Select
                  id="audit-entity"
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                >
                  <option value="">{t("common:all")}</option>
                  {(filters?.entity_types ?? []).map((et) => (
                    <option key={et} value={et}>
                      {labelFor("entities", et)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="audit-action" value={t("auditLog:action")} />
                <Select
                  id="audit-action"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  <option value="">{t("common:all")}</option>
                  {(filters?.actions ?? []).map((a) => (
                    <option key={a} value={a}>
                      {labelFor("actions", a)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="audit-from" value={t("auditLog:from")} />
                <TextInput
                  id="audit-from"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="audit-to" value={t("auditLog:to")} />
                <TextInput
                  id="audit-to"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pb-8">
        {needsCompany ? (
          <SelectCompanyPrompt />
        ) : isLoading ? (
          <LoadingPage />
        ) : (
          <div className={`flex flex-col gap-4 ${isFetching ? "opacity-60" : ""}`}>
            <div className="relative overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="border-b border-gray-200 bg-gray-50/80 text-xs uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{t("auditLog:when")}</th>
                    <th className="px-4 py-3 font-semibold">{t("auditLog:who")}</th>
                    <th className="px-4 py-3 font-semibold">{t("auditLog:action")}</th>
                    <th className="px-4 py-3 font-semibold">{t("auditLog:entityType")}</th>
                    <th className="px-4 py-3 font-semibold">{t("auditLog:record")}</th>
                    <th className="px-4 py-3 font-semibold" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700/50 dark:bg-gray-800">
                  {data?.items?.length ? (
                    data.items.map((row) => (
                      <Fragment key={row.id}>
                        <tr
                          className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40"
                          onClick={() =>
                            setExpanded(expanded === row.id ? null : row.id)
                          }
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">
                            {formatDate(row.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            <div className="font-medium">
                              {row.actor_display_name ||
                                row.actor_username ||
                                t("auditLog:system")}
                            </div>
                            {row.actor_username && row.actor_display_name && (
                              <div className="text-xs text-gray-400">
                                @{row.actor_username}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              color={ACTION_COLORS[row.action] ?? "gray"}
                              className="w-fit"
                            >
                              {labelFor("actions", row.action)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            {labelFor("entities", row.entity_type)}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            <div>
                              {row.entity_id ? (
                                <IdButton entityType={row.entity_type} id={row.entity_id}>
                                  {row.entity_label ?? "—"}
                                </IdButton>
                              ) : (
                                row.entity_label ?? "—"
                              )}
                            </div>
                            {row.entity_id && (
                              <div className="text-xs text-gray-400">
                                <IdButton entityType={row.entity_type} id={row.entity_id}>
                                  #{row.entity_id}
                                </IdButton>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-400">
                            {expanded === row.id ? (
                              <HiChevronUp className="h-4 w-4" />
                            ) : (
                              <HiChevronDown className="h-4 w-4" />
                            )}
                          </td>
                        </tr>
                        {expanded === row.id && (
                          <tr className="bg-gray-50 dark:bg-gray-900/40">
                            <td colSpan={6} className="px-6 py-4">
                              <ChangeDetails entry={row} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-16 text-center text-gray-400">
                        {t("auditLog:empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {data?.pagination && (
              <TablePagination
                pagination={data.pagination}
                onPageChange={setPage}
              />
            )}
          </div>
        )}
      </div>
      </RecordOpenContext.Provider>
    </NavbarSidebarLayout>
  );
};

export default AuditLogPage;
